<template>
  <div class="file-upload-dropzone">
    <!-- Main Dropzone Area -->
    <div
      ref="dropzone"
      @click="openFileDialog"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
      class="relative border-2 border-dashed rounded-lg transition-colors duration-200 cursor-pointer"
      :class="dropzoneClasses"
      :aria-disabled="effectiveDisabled"
    >
      <!-- Upload Content -->
      <div
        class="flex flex-col items-center justify-center py-8 px-4"
        v-if="!isUploading && files.length === 0"
      >
        <!-- Upload Icon -->
        <div class="mb-4">
          <svg
            class="w-12 h-12 transition-colors duration-200"
            :class="isDragOver ? 'text-eorange-500' : 'text-gray-400'"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 48 48"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
            />
          </svg>
        </div>

        <!-- Upload Text -->
        <div class="text-center">
          <p class="mb-2 text-sm text-gray-600">
            <span class="font-semibold">Click para subir</span>
            o arrastre y suelte
          </p>
          <p class="text-xs text-gray-500">
            Archivos permitidos: {{ allowedTypesText }}
          </p>
          <p class="text-xs text-gray-500 mt-1">
            Tamaño máximo: {{ formatFileSize(maxFileSize) }}
            <span v-if="multiple"> • Máximo {{ maxFiles }} archivos</span>
          </p>
        </div>
      </div>

      <!-- File Previews -->
      <div v-else-if="files.length > 0" class="p-4">
        <div class="space-y-3">
          <div
            v-for="(fileData, index) in files"
            :key="fileData.id"
            class="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
          >
            <!-- File Info -->
            <div class="flex items-center space-x-3 flex-1 min-w-0">
              <!-- File Icon/Preview -->
              <div class="flex-shrink-0">
                <img
                  v-if="
                    fileData.preview && fileData.file.type.startsWith('image/')
                  "
                  :src="fileData.preview"
                  :alt="fileData.file.name"
                  class="w-12 h-12 object-cover rounded-lg border"
                />
                <div
                  v-else
                  class="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center"
                >
                  <svg
                    class="w-6 h-6 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
              </div>

              <!-- File Details -->
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-gray-900 truncate">
                  {{ fileData.file.name }}
                </p>
                <p class="text-xs text-gray-500">
                  {{ formatFileSize(fileData.file.size) }} •
                  {{ fileData.file.type }}
                </p>

                <!-- Upload Progress -->
                <div v-if="fileData.uploadProgress" class="mt-2">
                  <UploadProgress
                    :state="fileData.uploadProgress.state"
                    :progress="fileData.uploadProgress.progress"
                    :message="fileData.uploadProgress.message"
                    :error="fileData.uploadProgress.error?.message"
                    :show-progress="true"
                    :allow-retry="true"
                    @retry="retryFileUpload(index)"
                  />
                </div>
              </div>

              <!-- Actions -->
              <div class="flex items-center space-x-2">
                <!-- Remove File -->
                <button
                  @click.stop="removeFile(index)"
                  v-if="
                    !fileData.uploadProgress ||
                    fileData.uploadProgress.state !== 'uploading'
                  "
                  class="p-1 text-gray-400 hover:text-red-600 focus:outline-none focus:text-red-600 transition-colors"
                  :title="'Eliminar ' + fileData.file.name"
                >
                  <svg
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Add More Files Button -->
        <button
          v-if="multiple && remainingFileSlots > 0"
          @click="openFileDialog"
          :disabled="effectiveDisabled"
          class="mt-3 w-full py-2 px-4 border border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-eorange-500"
        >
          Agregar más archivos ({{ currentFileCount }}/{{ maxFiles }})
        </button>
      </div>

      <!-- Loading State -->
      <div
        v-if="isUploading"
        class="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-lg"
      >
        <div class="text-center">
          <svg
            class="animate-spin -ml-1 mr-3 h-8 w-8 text-eorange-500 mx-auto mb-2"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <p class="text-sm text-gray-600">Subiendo archivos...</p>
        </div>
      </div>
    </div>

    <!-- Hidden File Input -->
    <input
      ref="fileInput"
      type="file"
      :accept="acceptString"
      :multiple="multiple"
      :disabled="effectiveDisabled"
      @change="onFileInputChange"
      class="hidden"
    />

    <!-- Validation Errors -->
    <div v-if="validationErrors.length > 0" class="mt-2 space-y-1">
      <p
        v-for="(error, index) in validationErrors"
        :key="index"
        class="text-xs text-red-600"
      >
        {{ error }}
      </p>
    </div>
  </div>
</template>

<script>
import { computed } from "vue";
import { useAuthStore } from "@/domains/auth";
import UploadProgress from "./UploadProgress.vue";
import {
  useFileUpload,
  UploadState,
  ALLOWED_UPLOAD_FOLDERS,
} from "@/services/upload-service";

export default {
  name: "FileUploadDropzone",
  components: {
    UploadProgress,
  },
  props: {
    // Upload configuration
    folder: {
      type: String,
      required: true,
      validator: (value) => ALLOWED_UPLOAD_FOLDERS.includes(value),
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    maxFiles: {
      type: Number,
      default: 4,
    },
    reservedFileCount: {
      type: Number,
      default: 0,
    },
    uploadMetadata: {
      type: Object,
      default: null,
    },
    maxFileSize: {
      type: Number,
      default: 10 * 1024 * 1024, // 10MB
    },
    allowedTypes: {
      type: Array,
      default: () => [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
      ],
    },

    // Org context for storage API calls ('tenant' | 'global' | 'none')
    orgContext: {
      type: String,
      default: "tenant",
      validator: (value) => ["tenant", "global", "none"].includes(value),
    },

    // Auto-upload behavior
    autoUpload: {
      type: Boolean,
      default: true,
    },

    // Styling
    height: {
      type: String,
      default: "auto",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    "files-selected",
    "upload-start",
    "upload-progress",
    "upload-complete",
    "upload-error",
    "files-changed",
  ],
  data() {
    return {
      files: [], // Array of { id, file, tenantOrgId, preview, uploadProgress, storageId, downloadUrl, cancelled }
      isDragOver: false,
      isUploading: false,
      validationErrors: [],
      nextFileId: 1,
    };
  },
  computed: {
    dropzoneClasses() {
      return {
        "border-gray-300 bg-gray-50":
          !this.isDragOver && !this.effectiveDisabled,
        "border-eorange-500 bg-eorange-50":
          this.isDragOver && !this.effectiveDisabled,
        "border-gray-200 bg-gray-100 cursor-not-allowed":
          this.effectiveDisabled,
        "hover:border-eorange-400 hover:bg-eorange-50":
          !this.effectiveDisabled && !this.isDragOver,
      };
    },

    effectiveDisabled() {
      if (this.disabled) return true;
      if (this.orgContext === "tenant" && !this.activeTenantOrgId) return true;
      return false;
    },

    allowedTypesText() {
      const typeMap = {
        "image/jpeg": "JPG",
        "image/jpg": "JPG",
        "image/png": "PNG",
        "image/gif": "GIF",
        "image/webp": "WEBP",
        "application/pdf": "PDF",
      };
      return this.allowedTypes.map((type) => typeMap[type] || type).join(", ");
    },

    acceptString() {
      return this.allowedTypes.join(",");
    },

    currentFileCount() {
      return Math.max(0, this.reservedFileCount) + this.pendingFileCount;
    },

    remainingFileSlots() {
      return Math.max(0, this.maxFiles - this.currentFileCount);
    },

    pendingFileCount() {
      return this.files.filter((fileData) => this.isPendingFile(fileData))
        .length;
    },
  },
  setup() {
    const authStore = useAuthStore();
    const activeTenantOrgId = computed(() => {
      const membership = authStore.currentMembership;
      return membership ? String(membership.org_id) : null;
    });
    const {
      createAndUploadFile,
      validateFile,
      formatFileSize,
      showSuccess,
      showError,
    } = useFileUpload();

    return {
      createAndUploadFile,
      validateFile,
      formatFileSize,
      showSuccess,
      showError,
      activeTenantOrgId,
      UploadState,
    };
  },
  watch: {
    activeTenantOrgId(newOrgId, oldOrgId) {
      if (oldOrgId !== undefined && newOrgId !== oldOrgId) {
        this.clearFiles();
      }
    },
  },
  methods: {
    openFileDialog() {
      if (!this.effectiveDisabled) {
        this.$refs.fileInput.click();
      }
    },

    onDragOver() {
      if (!this.effectiveDisabled) {
        this.isDragOver = true;
      }
    },

    onDragLeave(event) {
      // Only set isDragOver to false if we're leaving the dropzone entirely
      if (!this.$refs.dropzone.contains(event.relatedTarget)) {
        this.isDragOver = false;
      }
    },

    onDrop(event) {
      this.isDragOver = false;
      if (!this.effectiveDisabled) {
        const droppedFiles = Array.from(event.dataTransfer.files);
        this.handleFiles(droppedFiles);
      }
    },

    onFileInputChange(event) {
      if (this.effectiveDisabled) {
        event.target.value = "";
        return;
      }

      const selectedFiles = Array.from(event.target.files);
      this.handleFiles(selectedFiles);
      // Reset input to allow selecting the same file again
      event.target.value = "";
    },

    async handleFiles(newFiles) {
      this.validationErrors = [];

      if (this.effectiveDisabled) {
        return;
      }

      // Check file count limits
      if (!this.multiple && newFiles.length > 1) {
        this.validationErrors.push("Solo se puede seleccionar un archivo");
        return;
      }

      if (this.multiple && newFiles.length > this.remainingFileSlots) {
        this.validationErrors.push(
          `Máximo ${this.maxFiles} archivos permitidos`,
        );
        return;
      }

      // Validate and process each file
      const validFiles = [];
      for (const file of newFiles) {
        try {
          const validation = await this.validateFile(file, {
            maxSize: this.maxFileSize,
            allowedTypes: this.allowedTypes,
          });

          if (validation.isValid) {
            const fileData = {
              id: this.nextFileId++,
              file,
              tenantOrgId: this.activeTenantOrgId,
              preview: file.type.startsWith("image/")
                ? URL.createObjectURL(file)
                : null,
              uploadProgress: null,
              storageId: null,
              downloadUrl: null,
              cancelled: false,
            };
            validFiles.push(fileData);
          } else {
            this.validationErrors.push(
              `${file.name}: ${validation.errors.join(", ")}`,
            );
          }
        } catch (error) {
          this.validationErrors.push(`${file.name}: Error de validación`);
        }
      }

      if (validFiles.length === 0) {
        return;
      }

      // Add files to the list
      if (this.multiple) {
        this.files.push(...validFiles);
      } else {
        // For single file, replace existing
        this.clearFiles();
        this.files = validFiles;
      }

      this.$emit("files-selected", validFiles);
      this.$emit("files-changed", this.files);

      // Auto-upload if enabled
      if (this.autoUpload) {
        await this.uploadFiles(validFiles);
      }
    },

    async uploadFiles(filesToUpload = null) {
      if (this.effectiveDisabled) {
        return;
      }

      const targetFiles = (
        filesToUpload || this.files.filter((f) => !f.storageId)
      ).filter((fileData) => this.isUploadActive(fileData));

      if (targetFiles.length === 0) {
        return;
      }

      this.isUploading = true;
      this.$emit("upload-start", targetFiles);

      const uploadPromises = targetFiles.map((fileData) =>
        this.uploadSingleFile(fileData, targetFiles),
      );

      try {
        await Promise.allSettled(uploadPromises);
      } finally {
        this.isUploading = false;
      }

      const results = this.getUploadResults(targetFiles);
      this.$emit("upload-complete", results);

      if (results.successful.length > 0) {
        this.showSuccess(
          `${results.successful.length} archivo(s) subido(s) correctamente`,
        );
      }
    },

    async uploadSingleFile(fileData, batchFiles = [fileData]) {
      if (!this.isUploadActive(fileData)) {
        return null;
      }

      try {
        const result = await this.createAndUploadFile(fileData.file, {
          folder: this.folder,
          orgContext: this.orgContext,
          metadata: this.uploadMetadata || undefined,
          onProgress: (progress) => {
            if (!this.isUploadActive(fileData)) {
              return;
            }
            fileData.uploadProgress = progress;
            this.$emit(
              "upload-progress",
              fileData,
              progress,
              this.getUploadProgressSummary(batchFiles),
            );
            this.$forceUpdate(); // Ensure reactivity
          },
          onStateChange: (state) => {
            if (!this.isUploadActive(fileData)) {
              return;
            }
            if (fileData.uploadProgress) {
              fileData.uploadProgress.state = state;
            }
            this.$forceUpdate(); // Ensure reactivity
          },
        });

        if (!this.isUploadActive(fileData)) {
          return null;
        }

        // Update file data with upload results
        fileData.storageId = result.storageId;
        fileData.downloadUrl = result.downloadUrl;
        if (fileData.uploadProgress) {
          fileData.uploadProgress.storageId = result.storageId;
          fileData.uploadProgress.downloadUrl = result.downloadUrl;
        }

        return result;
      } catch (error) {
        if (!this.isUploadActive(fileData)) {
          return null;
        }

        console.error("Upload failed for file:", fileData.file.name, error);

        if (fileData.uploadProgress) {
          fileData.uploadProgress.setError(error);
        } else {
          fileData.uploadProgress = {
            state: UploadState.ERROR,
            progress: 0,
            message: error.message,
            error,
          };
        }

        this.$emit("upload-error", fileData, error);
        this.showError(
          `Error subiendo ${fileData.file.name}: ${error.message}`,
        );

        throw error;
      }
    },

    async retryFileUpload(fileIndex) {
      if (this.effectiveDisabled) {
        return;
      }

      const fileData = this.files[fileIndex];
      if (fileData) {
        // Reset upload progress
        fileData.uploadProgress = null;
        fileData.storageId = null;
        fileData.downloadUrl = null;

        await this.uploadFiles([fileData]);
      }
    },

    removeFile(index) {
      const fileData = this.files[index];
      if (!fileData) {
        return;
      }

      fileData.cancelled = true;

      // Revoke preview URL to free memory
      if (fileData.preview) {
        URL.revokeObjectURL(fileData.preview);
      }

      this.files.splice(index, 1);
      this.$emit("files-changed", this.files);
    },

    clearCompletedFiles() {
      this.clearFilesMatching((fileData) => this.isUploadCompleted(fileData));
    },

    clearFilesMatching(predicate) {
      const removedFiles = [];
      const remainingFiles = [];

      this.files.forEach((fileData) => {
        if (predicate(fileData)) {
          fileData.cancelled = true;
          removedFiles.push(fileData);
        } else {
          remainingFiles.push(fileData);
        }
      });

      if (removedFiles.length === 0) {
        return;
      }

      removedFiles.forEach((fileData) => {
        if (fileData.preview) {
          URL.revokeObjectURL(fileData.preview);
        }
      });

      this.files = remainingFiles;
      this.$emit("files-changed", this.files);
    },

    clearFiles() {
      // Clean up preview URLs
      this.files.forEach((fileData) => {
        fileData.cancelled = true;
        if (fileData.preview) {
          URL.revokeObjectURL(fileData.preview);
        }
      });

      this.files = [];
      this.validationErrors = [];
      this.$emit("files-changed", this.files);
    },

    getUploadResults(files = this.files) {
      return {
        successful: files
          .filter(
            (f) => this.isUploadResultActive(f) && this.isUploadCompleted(f),
          )
          .map((f) => this.toUploadResult(f)),
        failed: files
          .filter((f) => this.isUploadResultActive(f) && this.isUploadFailed(f))
          .map((f) => this.toUploadResult(f)),
        storageIds: files
          .filter(
            (f) =>
              this.isUploadResultActive(f) &&
              this.isUploadCompleted(f) &&
              f.storageId,
          )
          .map((f) => f.storageId),
        downloadUrls: files
          .filter(
            (f) =>
              this.isUploadResultActive(f) &&
              this.isUploadCompleted(f) &&
              f.downloadUrl,
          )
          .map((f) => f.downloadUrl),
      };
    },

    getUploadProgressSummary(files = this.files) {
      const total = files.length;
      const completedCount = files.filter((fileData) =>
        this.isUploadCompleted(fileData),
      ).length;
      const failedCount = files.filter((fileData) =>
        this.isUploadFailed(fileData),
      ).length;
      const progressTotal = files.reduce((sum, fileData) => {
        return sum + (fileData.uploadProgress?.progress || 0);
      }, 0);

      return {
        total,
        completedCount,
        failedCount,
        overall: total > 0 ? Math.round(progressTotal / total) : 0,
      };
    },

    isUploadCompleted(fileData) {
      return Boolean(
        fileData.storageId &&
          (!fileData.uploadProgress ||
            fileData.uploadProgress.state === UploadState.COMPLETED),
      );
    },

    isUploadFailed(fileData) {
      return fileData.uploadProgress?.state === UploadState.ERROR;
    },

    isPendingFile(fileData) {
      return !fileData.cancelled && !this.isUploadCompleted(fileData);
    },

    isUploadActive(fileData) {
      return Boolean(
        fileData &&
          !fileData.cancelled &&
          fileData.tenantOrgId === this.activeTenantOrgId &&
          this.files.some((file) => file.id === fileData.id),
      );
    },

    isUploadResultActive(fileData) {
      return this.isUploadActive(fileData);
    },

    toUploadResult(fileData) {
      return {
        id: fileData.id,
        file: fileData.file,
        tenantOrgId: fileData.tenantOrgId,
        preview: fileData.preview,
        storageId: fileData.storageId,
        storage_id: fileData.storageId,
        downloadUrl: fileData.downloadUrl,
        download_url: fileData.downloadUrl,
        originalName: fileData.file?.name,
        size: fileData.file?.size,
        mimeType: fileData.file?.type,
        error: fileData.uploadProgress?.error || null,
      };
    },

    // Public methods for parent components
    startUpload() {
      return this.uploadFiles();
    },

    getFiles() {
      return this.files;
    },

    getStorageIds() {
      return this.files.filter((f) => f.storageId).map((f) => f.storageId);
    },
  },

  beforeUnmount() {
    // Clean up preview URLs
    this.clearFiles();
  },
};
</script>

<style scoped>
.file-upload-dropzone {
  @apply w-full;
}

/* Custom scrollbar for file list */
.space-y-3 {
  max-height: 300px;
  overflow-y: auto;
}

.space-y-3::-webkit-scrollbar {
  width: 6px;
}

.space-y-3::-webkit-scrollbar-track {
  @apply bg-gray-100 rounded;
}

.space-y-3::-webkit-scrollbar-thumb {
  @apply bg-gray-300 rounded;
}

.space-y-3::-webkit-scrollbar-thumb:hover {
  @apply bg-gray-400;
}
</style>
